/**
 * Keyword-driven screenshot picker for SEO landing pages.
 *
 * Every landing page renders through <SeoLandingPage>, which asks
 * `pickScreenshotsForSlug(slug)` for 1-3 relevant product screenshots so
 * the page shows what the software actually looks like (Google's page
 * experience signals + user trust). The map is deliberately explicit
 * rather than fuzzy — a bad screenshot is worse than none.
 *
 * All paths must be real files under /app/frontend/public/. Screenshots
 * are grouped by topic; the first rule that matches the slug wins.
 */

const SHOTS = {
  dashboard: {
    src: '/sales-primer-shots/02-sales-orders-pipeline.png',
    alt: 'Keystone Sales Orders pipeline (Public Sandbox demo data), draft / in-progress / locked / dispatched columns with per-customer counts.',
    caption: 'A live-updating pipeline of every order, drawn from the Public Sandbox demo dataset.',
  },
  batches: {
    src: '/sales-primer-shots/04-batches-list.png',
    alt: 'Keystone Batches (Public Sandbox demo data), production runs by product with per-batch counts.',
    caption: 'Every make-day, every lot, every input weight. One place.',
  },
  dispatches: {
    src: '/sales-primer-shots/05-dispatches-list.png',
    alt: 'Keystone Dispatches (Public Sandbox demo data), filterable by customer / PO / status with per-line batch codes and dispatch weights.',
    caption: 'Dispatches with batch codes and pallet weights on the line, not stapled to the back.',
  },
  recall: {
    src: '/product/mock-recall.png',
    alt: 'Keystone Mock Recall report (Public Sandbox demo data), full forward trace from a supplier lot to every affected batch, customer and dispatch.',
    caption: 'One click. Every customer, every branch, every quantity, ready as a signed PDF.',
  },
  intake: {
    src: '/product/inwards-milk-intake.png',
    alt: 'Keystone Milk Intake (Public Sandbox demo data), raw milk intakes with volume, supplier, silo destination and antibiotic pass/fail.',
    caption: 'Goods-in becomes the first link in the traceability chain, not a paper docket in a folder.',
  },
  reports: {
    src: '/sales-primer-shots/06-reports.png',
    alt: 'Keystone Reports (Public Sandbox demo data), make-day summary with milk usage, ingredient / packaging usage and batch counts by supervisor.',
    caption: 'Reports built for QA managers and auditors, not for looking pretty in board meetings.',
  },
  orders: {
    src: '/product/order-entry.png',
    alt: 'Keystone new sales order (Public Sandbox demo data), customer, PO number, dispatch date, transport and line items with catch-weight units.',
    caption: 'Order entry that speaks retailer SKUs and your internal codes at the same time.',
  },
  customers: {
    src: '/sales-primer-shots/09-customers.png',
    alt: 'Keystone Customers (Public Sandbox demo data), retailer codes, delivery windows and private-label SKU mappings.',
    caption: 'Every customer\u2019s code, every private-label re-code, mapped once and for all.',
  },
  skuMap: {
    src: '/sales-primer-shots/10-supplier-codes.png',
    alt: 'Keystone SKU mapping (Public Sandbox demo data), your product code alongside every retailer\u2019s code for the same SKU.',
    caption: 'One SKU can have five retailer codes. Keystone speaks all of them.',
  },
  dispatchNoteOpen: {
    src: '/product/dispatches.png',
    alt: 'Keystone dispatch note editor (Public Sandbox demo data), customer header, PO number, delivery address, line items with batch codes and use-by dates.',
    caption: 'What the driver hands over. What the retailer\u2019s goods-in team scans. What the auditor sees.',
  },
  recallRun: {
    src: '/sales-primer-shots/14-recall-run.jpg',
    alt: 'Keystone Recall run (Public Sandbox demo data), 90-second full forward trace with total kilos affected and customer contact list.',
    caption: 'From \u201cwhich batch?\u201d to \u201cwhich customers and how many kilos\u201d in 90 seconds.',
  },
  backwardTrace: {
    src: '/product/compliance.png',
    alt: 'Keystone Backward Trace (Public Sandbox demo data), sales order to dispatch to cheese batch to make-day to milk intake to supplier chain of custody.',
    caption: 'Sales order to supplier lot, walked backward step by step, in a single view.',
  },
  compliance: {
    src: '/product/compliance-report.png',
    alt: 'Keystone Traceability Events (Public Sandbox demo data), audit evidence pack with QC, mass balance, mock-recall history and signed PDF export.',
    caption: 'The audit binder assembled itself. You just tell it what date range.',
  },
};

/**
 * Rule table: first matching rule wins. Each rule is a list of keywords
 * (matched as substrings on the slug) and the ordered set of screenshots
 * to render for that page.
 */
const RULES = [
  { match: ['recall'], picks: ['recall', 'recallRun', 'batches'] },
  { match: ['dispatch', 'pallet-label', 'gs1-128', 'sscc'], picks: ['dispatchNoteOpen', 'dispatches', 'batches'] },
  { match: ['coa', 'certificate-of-analysis'], picks: ['batches', 'compliance', 'reports'] },
  { match: ['goods-in', 'intake', 'inward'], picks: ['intake', 'batches', 'reports'] },
  { match: ['batch-tracking', 'batch-traceability', 'batch-'], picks: ['batches', 'backwardTrace', 'recall'] },
  { match: ['customer-sku', 'sku-mapping', 'retailer-dispatch'], picks: ['skuMap', 'customers', 'dispatchNoteOpen'] },
  { match: ['brcgs', 'salsa', 'haccp', 'allergen', 'audit'], picks: ['compliance', 'recall', 'reports'] },
  { match: ['excel-vs', 'alternative', 'vs-', 'competitor', '-keystone'], picks: ['dashboard', 'batches', 'dispatches'] },
  { match: ['cheese', 'dairy', 'farmhouse', 'artisan'], picks: ['batches', 'intake', 'dispatchNoteOpen'] },
  { match: ['bakery', 'meat', 'seafood', 'prepared-food'], picks: ['batches', 'dispatches', 'reports'] },
  { match: ['food-erp', 'production-planning', 'wholesale'], picks: ['dashboard', 'batches', 'orders'] },
  { match: ['traceability'], picks: ['backwardTrace', 'recall', 'dispatchNoteOpen'] },
];

const DEFAULT_PICKS = ['dashboard', 'batches', 'dispatches'];

export function pickScreenshotsForSlug(slug) {
  if (!slug) return DEFAULT_PICKS.map((k) => SHOTS[k]);
  const s = String(slug).toLowerCase();
  const rule = RULES.find((r) => r.match.some((kw) => s.includes(kw)));
  const keys = rule ? rule.picks : DEFAULT_PICKS;
  return keys.map((k) => SHOTS[k]).filter(Boolean);
}


/**
 * Auto-derive a mid-page internal link strip if the page doesn't define
 * its own. Groups pages by topic family (traceability, comparison,
 * industry vertical, certification) using the slug, then picks up to
 * 4 siblings — never the current page.
 *
 * `cfg.internalLinks` on a landing page config wins over auto-derivation.
 */
const TOPIC_FAMILIES = [
  {
    key: 'recall',
    label: 'Recall management',
    matches: (s) => /recall/.test(s),
  },
  {
    key: 'dispatch',
    label: 'Dispatch & labelling',
    matches: (s) => /(dispatch|pallet-label|gs1-128|sscc)/.test(s),
  },
  {
    key: 'traceability',
    label: 'Traceability',
    matches: (s) => /traceability/.test(s),
  },
  {
    key: 'comparison',
    label: 'How Keystone compares',
    matches: (s) => /(^vs-|-vs-|-alternative$|excel-vs|-keystone$)/.test(s),
  },
  {
    key: 'dairy',
    label: 'Dairy & cheese producers',
    matches: (s) => /(dairy|cheese|farmhouse|artisan)/.test(s),
  },
  {
    key: 'vertical',
    label: 'Your industry',
    matches: (s) => /(bakery|meat|seafood|prepared-food|wholesale)/.test(s),
  },
  {
    key: 'audit',
    label: 'Audits & certifications',
    matches: (s) => /(brcgs|salsa|haccp|allergen|audit|coa)/.test(s),
  },
];

export function pickInternalLinksForSlug(slug, allPagesBySlug) {
  if (!allPagesBySlug || !slug) return [];
  const s = String(slug).toLowerCase();
  const family = TOPIC_FAMILIES.find((f) => f.matches(s));
  const all = Object.values(allPagesBySlug);
  const siblings = family
    ? all.filter((p) => p.slug !== slug && family.matches(String(p.slug).toLowerCase()))
    : [];
  // Top up with a curated cross-section if the family has fewer than 4
  const backfill = all.filter(
    (p) => p.slug !== slug && !siblings.includes(p) && (
      p.slug === 'food-traceability-software-ireland'
      || p.slug === 'batch-traceability-software'
      || p.slug === 'mock-recall-software-food-producers'
      || p.slug === 'brcgs-traceability-software'
      || p.slug === 'food-dispatch-note-pallet-label-software'
    )
  );
  const merged = [...siblings, ...backfill].slice(0, 4);
  return merged.map((p) => ({
    label: p.title,
    to: `/${p.slug}`,
    blurb: p.metaDescription,
  }));
}
